import { debounce } from 'debounce';
import { useCallback, useEffect, useState, useRef, MouseEventHandler } from 'react';

interface Props {
  minQuantity: number;
  maxQuantity: number;
  quantity: number;
  showRemove?: boolean;
  instantUpdate?: boolean;
  onRemoveFromCart?: () => any;
  onSetQuantity: (quantity: number) => any;
  extraClassname?: string;
  removeFromCartBtnText?: string;
  removeFromCartConfirmBtnText?: string;
  removeFromCartDialogText?: string;
  batchQuantity: number;
}

const dialogOpenEvtName = 'dialogOpen';

const QuantitySelector = ({
  removeFromCartBtnText,
  removeFromCartConfirmBtnText,
  removeFromCartDialogText,
  instantUpdate,
  minQuantity,
  maxQuantity,
  quantity,
  showRemove,
  onRemoveFromCart,
  onSetQuantity,
  extraClassname,
  batchQuantity,
}: Props) => {
  const [localQuantity, setLocalQuantity] = useState(quantity);
  const [showCfmDialog, setShowCfmDialog] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClickOutside = (e: MouseEvent) => {
    if (dialogRef.current && !dialogRef.current.contains(e.target as Node) && !buttonRef.current?.contains(e.target as Node)) {
      setShowCfmDialog(false);
    }
  };

  const handleClickInside: MouseEventHandler = (e) => {
    e.stopPropagation();
  };

  const debouncedChangeHandler = useCallback(
    debounce((newQuantity: number, quantity: number) => {
      if (newQuantity !== quantity) {
        onSetQuantity(newQuantity);
      }
    }, 1000),
    []
  );

  const handleCustomEvent = ((e: CustomEvent<any>) => {
    if (e.type !== dialogOpenEvtName) return;
    const ref = e.detail as Node;
    if (!buttonRef.current?.contains(ref)) {
      setShowCfmDialog(false);
    }
  }) as EventListener;

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener(dialogOpenEvtName, handleCustomEvent);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener(dialogOpenEvtName, handleCustomEvent);
      debouncedChangeHandler.clear();
    };
  }, []);

  useEffect(() => {
    if (instantUpdate) {
      onSetQuantity(localQuantity);
    } else {
      debouncedChangeHandler(localQuantity, quantity);
    }
  }, [localQuantity, quantity]);

  const handleDecreaseQuantity = () => {
    const newQuantity = localQuantity > minQuantity ? localQuantity - batchQuantity : minQuantity;
    setLocalQuantity(Math.max(newQuantity, minQuantity));
  };

  const handleIncreaseQuantity = () => {
    const newQuantity = maxQuantity === 0 || localQuantity < maxQuantity ? localQuantity + batchQuantity : maxQuantity;
    setLocalQuantity(Math.min(newQuantity, maxQuantity));
  };

  const handleRemoveFromCart: MouseEventHandler = (e) => {
    e.stopPropagation();
    if (!showCfmDialog) {
      setShowCfmDialog(true);
      document.dispatchEvent(
        new CustomEvent(dialogOpenEvtName, {
          detail: buttonRef.current,
        })
      );
    } else {
      setShowCfmDialog(false);
    }
  };

  const handleConfirmRemove: MouseEventHandler = (e) => {
    e.stopPropagation();

    setShowCfmDialog(false);
    if (onRemoveFromCart) {
      onRemoveFromCart();
    }
  };

  const handleNavbarClick = () => {
    setShowCfmDialog(false);
  };

  return (
    <div className={`fm-o-quantity-selector tfm ${extraClassname ?? ''}`}>
      <div className="fm-o-quantity-selector__container" translate="no">
        <div className="fm-o-quantity-selector__buttons">
          <button className="fm-o-quantity-selector__btn tfm" onClick={handleDecreaseQuantity} disabled={localQuantity <= minQuantity}>
            <svg className="fm-o-quantity-selector__svg" width="9" height="2" viewBox="0 0 9 2" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 1H1" stroke="currentColor" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <div className="fm-o-quantity-selector__quantity">{localQuantity}</div>
          <button
            className="fm-o-quantity-selector__btn tfm"
            onClick={handleIncreaseQuantity}
            disabled={maxQuantity === 0 || (maxQuantity > 0 && localQuantity >= maxQuantity)}
          >
            <svg className="fm-o-quantity-selector__svg" width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8 4.5H1M4.5 1V8V1Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        {showRemove && (
          <button className="fm-o-quantity-selector__remove-from-cart tfm" onClick={handleRemoveFromCart} ref={buttonRef}>
            <svg width="19" height="22" viewBox="0 0 19 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M16.2019 3.92312L15.2538 19.3212C15.2455 19.7434 15.074 20.1461 14.7754 20.4447C14.4768 20.7433 14.0742 20.9148 13.6519 20.9231H5.34808C4.92584 20.9148 4.52323 20.7433 4.2246 20.4447C3.92598 20.1461 3.75452 19.7434 3.74615 19.3212L2.79808 3.92312M1 3.92312H18H1ZM6.23077 3.92312V2.58274C6.23077 2.24459 6.3651 1.92029 6.60421 1.68118C6.84332 1.44207 7.16762 1.30774 7.50577 1.30774H11.4942C11.6617 1.30774 11.8275 1.34072 11.9822 1.40479C12.1368 1.46887 12.2774 1.56278 12.3958 1.68118C12.5142 1.79957 12.6081 1.94013 12.6722 2.09482C12.7363 2.24951 12.7692 2.4153 12.7692 2.58274V3.92312H6.23077Z"
                stroke="#2D3047"
                stroke-width="1.61053"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M12.7689 6.53845L12.1151 18.3077M9.4997 6.53845V18.3077V6.53845ZM6.23047 6.53845L6.88431 18.3077L6.23047 6.53845Z"
                stroke="#2D3047"
                stroke-width="1.61053"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div>{removeFromCartBtnText || 'Remove from cart'}</div>

            <div
              className={`fm-o-quantity-selector__remove-from-cart-dialog ${
                showCfmDialog ? 'fm-o-quantity-selector__remove-from-cart-dialog--active' : ''
              }`}
            >
              <div className="fm-o-quantity-selector__remove-from-cart-dialog-backdrop">
                <div className="fm-o-quantity-selector__remove-from-cart-dialog-body" ref={dialogRef} onClick={handleClickInside}>
                  <div className="fm-o-quantity-selector__remove-from-cart-dialog-navbar" onClick={handleNavbarClick}></div>
                  <div className="fm-o-quantity-selector__remove-from-cart-dialog-text">
                    {removeFromCartDialogText || 'Are you sure you want to remove this item?'}
                  </div>
                  <button
                    className="fm-o-quantity-selector__remove-from-cart-dialog-btn tfm fm-a-button fm-a-button--red fm-a-button--secondary"
                    onClick={handleConfirmRemove}
                  >
                    <svg width="19" height="22" viewBox="0 0 19 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M16.2019 3.92312L15.2538 19.3212C15.2455 19.7434 15.074 20.1461 14.7754 20.4447C14.4768 20.7433 14.0742 20.9148 13.6519 20.9231H5.34808C4.92584 20.9148 4.52323 20.7433 4.2246 20.4447C3.92598 20.1461 3.75452 19.7434 3.74615 19.3212L2.79808 3.92312M1 3.92312H18H1ZM6.23077 3.92312V2.58274C6.23077 2.24459 6.3651 1.92029 6.60421 1.68118C6.84332 1.44207 7.16762 1.30774 7.50577 1.30774H11.4942C11.6617 1.30774 11.8275 1.34072 11.9822 1.40479C12.1368 1.46887 12.2774 1.56278 12.3958 1.68118C12.5142 1.79957 12.6081 1.94013 12.6722 2.09482C12.7363 2.24951 12.7692 2.4153 12.7692 2.58274V3.92312H6.23077Z"
                        stroke="currentColor"
                        stroke-width="1.61053"
                        stroke-miterlimit="10"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12.7689 6.53845L12.1151 18.3077M9.4997 6.53845V18.3077V6.53845ZM6.23047 6.53845L6.88431 18.3077L6.23047 6.53845Z"
                        stroke="currentColor"
                        stroke-width="1.61053"
                        stroke-miterlimit="10"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <div className="fm-o-quantity-selector__remove-from-cart-dialog-btn-text">
                      {removeFromCartConfirmBtnText || 'Yes, remove from cart'}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

export default QuantitySelector;
